import React, { useRef, useState } from "react";
import { VideoCallInterface } from "@/components/video-call/VideoCallInterface";
import { Lead, MediaState, ConversationMessage } from "@/types/video-call";

/**
 * VideoCallPreviewPage - A standalone page for testing the VideoCallInterface UI
 * without requiring actual AI agents or call setup.
 *
 * Access via: /video-call-preview
 */
const VideoCallPreviewPage: React.FC = () => {
  // Mock lead data
  const mockLead: Lead = {
    id: "preview-lead-123",
    first_name: "John",
    last_name: "Doe",
    email: "john.doe@example.com",
    phone_number: "+1234567890",
  };

  // Mock conversation with sample messages
  const mockConversation: ConversationMessage[] = [
    {
      role: "assistant",
      message: "Hello! Thank you for joining this interview. Can you please tell me about your background?",
      timestamp: new Date(Date.now() - 120000).toISOString(),
    },
    {
      role: "user",
      message: "Sure! I have 5 years of experience in software development, primarily working with React and TypeScript.",
      timestamp: new Date(Date.now() - 90000).toISOString(),
    },
    {
      role: "assistant",
      message: "That's great! Can you describe a challenging project you've worked on recently?",
      timestamp: new Date(Date.now() - 60000).toISOString(),
    },
    {
      role: "user",
      message: "I recently built a real-time video calling platform with advanced monitoring features. It involved integrating LiveKit for WebRTC communication.",
      timestamp: new Date(Date.now() - 30000).toISOString(),
    },
    {
      role: "assistant",
      message: "Excellent! What were some of the technical challenges you faced during that project?",
      timestamp: new Date(Date.now() - 10000).toISOString(),
    },
  ];

  // Mock media state
  const mockMediaState: MediaState = {
    isMuted: false,
    isVideoEnabled: true,
    isSpeakerMuted: false,
    screenSharingActive: false,
    screenShare: null,
    screenShareInterrupted: false,
    callPaused: false,
  };

  // Video refs
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const remoteAudioRef = useRef<HTMLAudioElement>(null);
  const screenShareRef = useRef<HTMLVideoElement>(null);
  const siriWaveRef = useRef<HTMLDivElement>(null);

  // UI state
  const [showSubtitles, setShowSubtitles] = useState(true);
  const [isAgentSpeaking, setIsAgentSpeaking] = useState(false);
  const [callDuration, setCallDuration] = useState("05:23");

  // Simulate agent speaking every few seconds
  React.useEffect(() => {
    const interval = setInterval(() => {
      setIsAgentSpeaking((prev) => !prev);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Mock handlers
  const handleToggleMicrophone = () => console.log("Toggle microphone");
  const handleToggleVideo = () => console.log("Toggle video");
  const handleToggleSpeaker = () => console.log("Toggle speaker");
  const handleToggleSubtitles = () => setShowSubtitles(!showSubtitles);
  const handleToggleChatWindow = () => console.log("Toggle chat window");
  const handleStartScreenShare = () => console.log("Start screen share");
  const handleStopScreenShare = () => console.log("Stop screen share");
  const handleEndCall = () => console.log("End call");

  return (
    <div className="h-screen w-screen">
      {/* Banner to indicate this is preview mode */}
      <div className="absolute top-0 left-0 right-0 z-50 bg-mid text-background text-center py-2 font-semibold">
        🎨 UI PREVIEW MODE - Mock Data Only
      </div>

      <div className="pt-10 h-full">
        <VideoCallInterface
          lead={mockLead}
          connected={true}
          connecting={false}
          callActive={true}
          remoteParticipants={[]}
          mediaState={mockMediaState}
          currentSubtitle={
            isAgentSpeaking
              ? "This is a sample subtitle showing what the agent is saying in real-time."
              : ""
          }
          showSubtitles={showSubtitles}
          isAgentSpeaking={isAgentSpeaking}
          conversation={mockConversation}
          showChatWindow={true}
          callDuration={callDuration}
          networkStatus="connected"
          connectionQuality="excellent"
          localVideoRef={localVideoRef}
          remoteVideoRef={remoteVideoRef}
          remoteAudioRef={remoteAudioRef}
          screenShareRef={screenShareRef}
          siriWaveRef={siriWaveRef}
          onToggleMicrophone={handleToggleMicrophone}
          onToggleVideo={handleToggleVideo}
          onToggleSpeaker={handleToggleSpeaker}
          onToggleSubtitles={handleToggleSubtitles}
          onToggleChatWindow={handleToggleChatWindow}
          onStartScreenShare={handleStartScreenShare}
          onStopScreenShare={handleStopScreenShare}
          onEndCall={handleEndCall}
          screenSharingEnabled={true}
          violations={[]}
          isInterviewSession={true}
          agentConnected={true}
          agentConnecting={false}
          agentDisconnected={false}
          showViolationsPanel={true}
        />
      </div>
    </div>
  );
};

export default VideoCallPreviewPage;
